import { BookOpen, CreditCard, Mail, RefreshCw, Scale, Shield, Truck, Users } from "lucide-react"

export interface TermsDocument {
    id: string
    title: string
    category: string
    content: string
    lastUpdated: string
    views: number
    icon: React.ElementType
    color: string
}

const termsDocuments: TermsDocument[] = [
    {
        id: '1',
        title: 'Términos Generales de Uso',
        category: 'general',
        icon: BookOpen,
        color: 'from-blue-500 to-blue-600',
        lastUpdated: '2024-12-15',
        views: 1250,
        content: `
            <h3 class="text-xl font-semibold text-blue-600 mb-2">1. Aceptación de Términos</h3>
            <p>Al acceder y utilizar SportCenter Shop, usted acepta cumplir con estos términos y condiciones. Si no está de acuerdo con alguno de estos términos, no debe utilizar nuestros servicios.</p>
            
            <h3 class="text-xl font-semibold text-blue-600 mb-2">2. Descripción del Servicio</h3>
            <p>SportCenter Shop es una plataforma de comercio electrónico especializada en suplementos deportivos, ropa deportiva, accesorios fitness y planes personalizados. Ofrecemos productos de calidad respaldados por experiencia y normativa local.</p>
            
            <h3 class="text-xl font-semibold text-blue-600 mb-2">3. Registro de Usuario</h3>
            <p>Para realizar compras debe crear una cuenta con información veraz y actualizada. Usted es responsable de mantener la confidencialidad de sus credenciales y de todas las actividades bajo su cuenta.</p>
            
            <h3 class="text-xl font-semibold text-blue-600 mb-2">4. Uso Apropiado</h3>
            <p>El usuario se compromete a utilizar nuestros servicios de manera legal, sin alterar el funcionamiento del sitio ni afectar a otros usuarios.</p>

            <h3 class="text-xl font-semibold text-blue-600 mb-2">5. Políticas de Uso Aceptable</h3>
            <p>Está prohibido el uso de bots, scraping, prácticas de fraude, manipulación de precios o cualquier acción que comprometa la seguridad del sistema.</p>

            <h3 class="text-xl font-semibold text-blue-600 mb-2">6. Restricciones de Cuenta</h3>
            <p>Nos reservamos el derecho de suspender cuentas que presenten actividad sospechosa, incumplan estos términos o infrinjan derechos de terceros.</p>

            <h3 class="text-xl font-semibold text-blue-600 mb-2">7. Suspensión y Terminación</h3>
            <p>SportCenter Shop podrá suspender temporal o permanentemente su cuenta si detecta violaciones graves de los términos o comportamientos ilícitos.</p>
        `,
    },
    {
        id: '2',
        title: 'Política de Privacidad',
        category: 'privacy',
        icon: Shield,
        color: 'from-green-500 to-green-600',
        lastUpdated: '2024-12-10',
        views: 890,
        content: `
            <h3 class="text-xl font-semibold text-green-600 mb-2">1. Recopilación de Información</h3>
            <p>Recopilamos información personal al registrarse, realizar compras o comunicarse con nosotros. Esto incluye datos como nombre, correo electrónico, dirección y método de pago.</p>
            
            <h3 class="text-xl font-semibold text-green-600 mb-2">2. Uso de la Información</h3>
            <p>La información se utiliza para procesar pedidos, mejorar la experiencia de usuario, enviar notificaciones y ofrecer recomendaciones personalizadas.</p>
            
            <h3 class="text-xl font-semibold text-green-600 mb-2">3. Protección de Datos</h3>
            <p>Implementamos medidas técnicas y organizativas para proteger sus datos frente a accesos no autorizados, pérdida o uso indebido.</p>

            <h3 class="text-xl font-semibold text-green-600 mb-2">4. Uso de Cookies</h3>
            <p>Utilizamos cookies para mejorar la navegación, personalizar el contenido y analizar el comportamiento de los usuarios en nuestro sitio.</p>

            <h3 class="text-xl font-semibold text-green-600 mb-2">5. Retención de Datos</h3>
            <p>Los datos se almacenan únicamente el tiempo necesario para cumplir con obligaciones legales y de servicio. Posteriormente serán eliminados o anonimizados.</p>

            <h3 class="text-xl font-semibold text-green-600 mb-2">6. Derechos del Usuario</h3>
            <p>Usted puede solicitar la corrección, eliminación o acceso a su información en cualquier momento mediante nuestros canales de soporte.</p>
        `,
    },
    {
        id: '3',
        title: 'Política de Pagos',
        category: 'payment',
        icon: CreditCard,
        color: 'from-purple-500 to-purple-600',
        lastUpdated: '2024-12-08',
        views: 675,
        content: `
            <h3 class="text-xl font-semibold text-purple-600 mb-2">1. Métodos de Pago Aceptados</h3>
            <p>Aceptamos tarjetas de crédito, débito, transferencias bancarias y pagos electrónicos vía PSE. Todos los pagos se realizan en pesos colombianos (COP).</p>
            
            <h3 class="text-xl font-semibold text-purple-600 mb-2">2. Procesamiento Seguro</h3>
            <p>Los pagos son gestionados por proveedores certificados. No almacenamos información completa de tarjetas en nuestros servidores.</p>
            
            <h3 class="text-xl font-semibold text-purple-600 mb-2">3. Verificación de Identidad</h3>
            <p>En casos de compras de alto valor, SportCenter Shop podrá solicitar documentación adicional para verificar la identidad del comprador.</p>

            <h3 class="text-xl font-semibold text-purple-600 mb-2">4. Reembolsos Parciales</h3>
            <p>En caso de devoluciones parciales, se reembolsará únicamente el valor proporcional del producto devuelto, descontando costos de envío.</p>

            <h3 class="text-xl font-semibold text-purple-600 mb-2">5. Errores de Facturación</h3>
            <p>Si identifica un error en su factura, debe notificarlo en un plazo máximo de 5 días hábiles tras la compra para su corrección.</p>
        `,
    },
    {
        id: '4',
        title: 'Política de Envíos',
        category: 'shipping',
        icon: Truck,
        color: 'from-orange-500 to-orange-600',
        lastUpdated: '2024-12-05',
        views: 1120,
        content: `
            <h3 class="text-xl font-semibold text-orange-600 mb-2">1. Cobertura Nacional</h3>
            <p>Los envíos se realizan únicamente dentro de Colombia. Los tiempos varían según la región: principales ciudades (2-4 días), municipios lejanos (3-7 días).</p>
            
            <h3 class="text-xl font-semibold text-orange-600 mb-2">2. Costos y Envío Gratis</h3>
            <p>Envío gratuito en compras mayores a $120.000 COP. Pedidos menores tendrán costos adicionales según peso y destino.</p>
            
            <h3 class="text-xl font-semibold text-orange-600 mb-2">3. Procesamiento</h3>
            <p>Los pedidos realizados después de las 4:00 PM serán procesados al siguiente día hábil. No se despachan en días festivos.</p>

            <h3 class="text-xl font-semibold text-orange-600 mb-2">4. Horarios de Entrega</h3>
            <p>Las entregas se realizan de lunes a sábado entre 8:00 AM y 7:00 PM. No garantizamos entregas en horarios exactos.</p>

            <h3 class="text-xl font-semibold text-orange-600 mb-2">5. Responsabilidad por Retrasos</h3>
            <p>SportCenter Shop no se hace responsable de retrasos ocasionados por factores externos como clima, paros o fuerza mayor.</p>

            <h3 class="text-xl font-semibold text-orange-600 mb-2">6. Pedidos No Entregados</h3>
            <p>Si un pedido no es recibido tras dos intentos de entrega, será devuelto a nuestro centro logístico. El cliente deberá asumir los costos de reenvío.</p>
        `,
    },
    {
        id: '5',
        title: 'Política de Devoluciones',
        category: 'returns',
        icon: RefreshCw,
        color: 'from-red-500 to-red-600',
        lastUpdated: '2024-12-03',
        views: 543,
        content: `
            <h3 class="text-xl font-semibold text-red-600 mb-2">1. Plazo de Devolución</h3>
            <p>Se aceptan devoluciones en un plazo de 7 días calendario desde la entrega, conforme a la normativa colombiana de retracto de compras online.</p>
            
            <h3 class="text-xl font-semibold text-red-600 mb-2">2. Condiciones</h3>
            <p>El producto debe estar sin abrir, en su empaque original y con todas las etiquetas. Los suplementos, por razones de seguridad alimentaria, no son elegibles para devolución una vez abiertos.</p>

            <h3 class="text-xl font-semibold text-red-600 mb-2">3. Productos No Elegibles</h3>
            <p>No se aceptan devoluciones de productos personalizados, ropa interior deportiva o artículos en promoción final.</p>

            <h3 class="text-xl font-semibold text-red-600 mb-2">4. Costos de Devolución</h3>
            <p>El cliente asume los costos de envío de la devolución, excepto en casos de error de SportCenter Shop o defectos de fábrica comprobados.</p>

            <h3 class="text-xl font-semibold text-red-600 mb-2">5. Reembolsos</h3>
            <p>Los reembolsos se realizarán en un plazo de 5-10 días hábiles a través del mismo método de pago utilizado.</p>
        `,
    },
    {
        id: '6',
        title: 'Responsabilidades del Usuario',
        category: 'user',
        icon: Users,
        color: 'from-teal-500 to-teal-600',
        lastUpdated: '2024-11-28',
        views: 432,
        content: `
            <h3 class="text-xl font-semibold text-teal-600 mb-2">1. Uso de Productos</h3>
            <p>Los productos deben usarse siguiendo las recomendaciones de fabricantes y especialistas en salud.</p>

            <h3 class="text-xl font-semibold text-teal-600 mb-2">2. Planes Personalizados</h3>
            <p>Los planes adquiridos son de uso individual y no deben ser compartidos, revendidos ni alterados.</p>

            <h3 class="text-xl font-semibold text-teal-600 mb-2">3. Edad Mínima</h3>
            <p>Solo mayores de 18 años pueden realizar compras. Los menores requieren autorización expresa de sus tutores legales.</p>

            <h3 class="text-xl font-semibold text-teal-600 mb-2">4. Prohibición de Reventa</h3>
            <p>Está prohibida la reventa de productos adquiridos en SportCenter Shop sin autorización expresa.</p>

            <h3 class="text-xl font-semibold text-teal-600 mb-2">5. Información Veraz</h3>
            <p>El usuario debe garantizar que toda la información proporcionada sea verídica y actualizada.</p>
        `,
    },
    {
        id: '7',
        title: 'Contacto y Soporte',
        category: 'contact',
        icon: Mail,
        color: 'from-indigo-500 to-indigo-600',
        lastUpdated: '2024-11-25',
        views: 789,
        content: `
            <h3 class="text-xl font-semibold text-indigo-600 mb-2">1. Canales Oficiales</h3>
            <p>Soporte disponible vía WhatsApp (+57 322 5432 580), correo (sportcenterchinchinabusiness@gmail.com) y formulario web.</p>

            <h3 class="text-xl font-semibold text-indigo-600 mb-2">2. Horarios</h3>
            <p>Atendemos de lunes a domingo, 8:00 AM - 11:00 PM. Respuestas promedio dentro de 24 horas hábiles.</p>

            <h3 class="text-xl font-semibold text-indigo-600 mb-2">3. Tipos de Consultas</h3>
            <p>Se atienden dudas de productos, pagos, envíos, devoluciones, soporte técnico y asesorías básicas.</p>

            <h3 class="text-xl font-semibold text-indigo-600 mb-2">4. Escalación</h3>
            <p>Casos complejos o reclamos formales se escalan a un equipo especializado. Tiempo máximo de resolución: 8 días hábiles.</p>
        `,
    },
    {
        id: '8',
        title: 'Aspectos Legales',
        category: 'legal',
        icon: Scale,
        color: 'from-gray-600 to-gray-700',
        lastUpdated: '2024-11-20',
        views: 321,
        content: `
            <h3 class="text-xl font-semibold text-gray-600 mb-2">1. Jurisdicción</h3>
            <p>Estos términos se rigen por la legislación colombiana. Cualquier disputa será tratada en los tribunales de Caldas, Colombia.</p>

            <h3 class="text-xl font-semibold text-gray-600 mb-2">2. Propiedad Intelectual</h3>
            <p>Todo el contenido de SportCenter Shop (textos, imágenes, logos y software) está protegido por derechos de autor y no puede ser utilizado sin autorización.</p>

            <h3 class="text-xl font-semibold text-gray-600 mb-2">3. Limitaciones de Garantía</h3>
            <p>No garantizamos que el sitio opere libre de errores o interrupciones. La responsabilidad máxima se limita al valor del producto adquirido.</p>

            <h3 class="text-xl font-semibold text-gray-600 mb-2">4. Resolución de Conflictos</h3>
            <p>Se promoverán soluciones amistosas mediante conciliación antes de acudir a instancias judiciales.</p>

            <h3 class="text-xl font-semibold text-gray-600 mb-2">5. Modificaciones</h3>
            <p>Nos reservamos el derecho de actualizar estas políticas en cualquier momento. La fecha de última actualización estará siempre disponible.</p>
        `,
    },
]

export { termsDocuments }
